// src/views/UserViews/insights/components/CoreProComparisonCard.tsx
//
// "Pro vs. Core Devices" card - distinct Compass Core vs. Pro device counts for the selected
// window, rendered as two peer stats side by side (Pro on the left, Core on the right,
// matching the title's word order). Not a `MetricCard` variant, for the same
// reason `UtilizationProgressCard` isn't one - see that file's own header comment: two
// independent numbers has no equivalent in MetricCard's single value/caption contract.
//
// Borrows `ComplianceTrendChart.tsx`'s `ComplianceHeadline` PATTERN (peer sizing + `flexWrap`,
// not the component itself - that function is private to its own file) for "two same-size
// numbers side by side".
//
// `coreDeviceCount`/`proDeviceCount` are plain non-nullable `number`s - a genuine `0` (e.g.
// an all-Pro org) is real data and renders as "0", never an em dash. See
// `CoreProDeviceCountSummary`'s own header comment (InsightsTypes.ts) for why a distinct-
// device count has no null case. Loading/error apply to the whole card (one Cube call feeds
// both sides), not per side.

import { Box, Card, CardContent, Skeleton, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import {
  INSIGHTS_CARD_BORDER_COLOR,
  INSIGHTS_CARD_BORDER_RADIUS,
  INSIGHTS_CARD_BOX_SHADOW,
} from './cardSurfaceStyles';

type CoreProComparisonCardProps = {
  /** Stable id for this card's data-testids - extended with a `-core`/`-pro` infix per side
   * below, since this card has two independent values. */
  testId: string;
  label: string;
  /** One-line definition shown in an info tooltip beside the label - this card has no
   * caption to merge it with (unlike MetricCard's hover reveal), so it stays a plain,
   * always-present icon+`Tooltip`. */
  tooltip?: string;
  /** Distinct count of Compass Core devices reporting in the selected window. Plain
   * `number`, never `null` - see this file's header comment. */
  coreDeviceCount: number;
  /** Distinct count of Compass Pro devices, same window - same non-nullable "0 is real data"
   * contract as `coreDeviceCount`, independent of it. */
  proDeviceCount: number;
  loading?: boolean;
  error?: Error | null;
};

// Matches MetricCard's own CARD_PADDING/CARD_MIN_HEIGHT so this card reads as a natural
// stack sibling, not a visual outlier.
const CARD_PADDING = 24;
const CARD_MIN_HEIGHT = 116;
const CARD_ROW_GAP = 10;
// Gap between the two peer stat blocks in the populated row below.
const STAT_COLUMN_GAP = 4;

const LABEL_FONT_WEIGHT = 700;

// Both stats share this size (peer sizing, see header comment) - matches MetricCard's own
// 30px `VALUE_FONT_SIZE` (not ComplianceTrendChart's larger 36px) since this card is a peer
// of its 30px stack-mates, not the page's single dominant trend headline.
const STAT_FONT_SIZE = '1.875rem'; // 30px
const STAT_FONT_WEIGHT = 700;
const STAT_LINE_HEIGHT = 1.1;

const SKELETON_VALUE_WIDTH = 96;
const SKELETON_VALUE_HEIGHT = 33;

const LABEL_SX = {
  fontWeight: LABEL_FONT_WEIGHT,
};

const STAT_VALUE_SX = {
  fontSize: STAT_FONT_SIZE,
  fontWeight: STAT_FONT_WEIGHT,
  lineHeight: STAT_LINE_HEIGHT,
  color: 'primary.main',
};

// This is the one card in UtilizationSection's stack deliberately stretched taller than its
// own content (see that file's "Column-height tradeoff" comment for why) - unlike its
// stack-mates (shared with other pages), it's safe to adapt this card's own layout to
// absorb the extra height. `flex: 1` lets this wrapper absorb it; `justifyContent:
// 'flex-start'` anchors stats to the top - a real screenshot showed centering left a big
// dead zone above the numbers once the card grew tall.
const VALUE_AREA_SX = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
} as const;

/** Thousands-separated integer, matching every other raw-count headline on this page - no
 * percent/decimals, since a distinct-device count is always a whole `Set.size`. */
function formatDeviceCount(count: number): string {
  return count.toLocaleString();
}

/** One peer stat block (value + its own "Compass Core"/"Compass Pro" sublabel underneath) -
 * extracted since the populated branch below renders exactly this shape twice, once per
 * side, with nothing else differing between the two besides `count`/`sideLabel`/`testId`. */
function DeviceCountStat({
  testId,
  count,
  sideLabel,
}: {
  testId: string;
  count: number;
  sideLabel: string;
}) {
  return (
    <Box>
      <Typography component="p" data-testid={`${testId}-value`} sx={STAT_VALUE_SX}>
        {formatDeviceCount(count)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {sideLabel}
      </Typography>
    </Box>
  );
}

export default function CoreProComparisonCard({
  testId,
  label,
  tooltip,
  coreDeviceCount,
  proDeviceCount,
  loading = false,
  error = null,
}: CoreProComparisonCardProps) {
  const { t } = useTranslation();

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        minHeight: CARD_MIN_HEIGHT,
        borderColor: INSIGHTS_CARD_BORDER_COLOR,
        borderRadius: `${INSIGHTS_CARD_BORDER_RADIUS}px`,
        boxShadow: INSIGHTS_CARD_BOX_SHADOW,
      }}
      data-testid={testId}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${CARD_ROW_GAP}px`,
          p: `${CARD_PADDING}px`,
          '&:last-child': { pb: `${CARD_PADDING}px` },
          // Without this, `Card` above can grow taller (once UtilizationSection.tsx
          // stretches this card's slot) while CardContent stays at its natural size and
          // sits at the top, leaving blank space inside Card's border - see VALUE_AREA_SX's
          // comment above for the sibling half of this fix.
          height: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="subtitle1" component="span" sx={LABEL_SX}>
            {label}
          </Typography>
          {tooltip && (
            <Tooltip title={tooltip} placement="top" arrow>
              <InfoOutlinedIcon
                aria-label={tooltip}
                sx={{ fontSize: '1rem', color: 'text.secondary' }}
              />
            </Tooltip>
          )}
        </Box>

        {/* `VALUE_AREA_SX` above absorbs any extra height; the label row stays pinned to the
         * top as a separate, non-growing sibling. `data-testid` lets tests locate this directly. */}
        <Box sx={VALUE_AREA_SX} data-testid={`${testId}-value-area`}>
          {loading && (
            <Skeleton
              variant="text"
              width={SKELETON_VALUE_WIDTH}
              height={SKELETON_VALUE_HEIGHT}
              data-testid={`${testId}-loading`}
            />
          )}

          {!loading && error && (
            <Typography variant="body2" color="error" data-testid={`${testId}-error`}>
              {t(StringKeys.INSIGHTS_SECTION_LOAD_ERROR)}
            </Typography>
          )}

          {/* Both sides always render once loading/error clear - no per-side null/em-dash
           * branch (see this file's header comment: 0 is real data). */}
          {!loading && !error && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                columnGap: STAT_COLUMN_GAP,
                rowGap: 2,
              }}
              // Lets InsightsMainRowAlignment.test.tsx measure this block directly, to
              // confirm VALUE_AREA_SX anchors it near the top, not just that the card grew.
              data-testid={`${testId}-stats`}
            >
              {/* Pro renders first (left) here, matching the "Pro vs. Core" title order -
               * testids stay `-pro`/`-core` regardless of position, so they keep identifying
               * the same class rather than "left"/"right". */}
              <DeviceCountStat
                testId={`${testId}-pro`}
                count={proDeviceCount}
                sideLabel={t(StringKeys.INSIGHTS_COMPASS_PRO_LABEL)}
              />
              <DeviceCountStat
                testId={`${testId}-core`}
                count={coreDeviceCount}
                sideLabel={t(StringKeys.INSIGHTS_COMPASS_CORE_LABEL)}
              />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
